import React, { useEffect, useState } from 'react';
import MetricSummaryTrendCardV2 from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardV2';
import MetricSummaryTrendCardLoading from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardLoading';
import { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import { ContentMetricType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';

interface ContentKeyMetricCardProps {
  variant: 'large' | 'small' | 'medium';
  indexName: string;
  fieldName: string;
  title?: string;
  onClick?: () => void;
}

/**
 * Converts TimeSeriesPoint array [weekId, value][] to SplineChartV2Series format
 */
const convertToSplineV2Series = (
  data: [number, number][],
  id: string,
  name: string
): SplineChartV2Series => ({
  id,
  name,
  data: data.map(([weekId, value]) => ({ weekId, value }))
});

const ContentKeyMetricCard = ({
  variant,
  title,
  onClick
}: ContentKeyMetricCardProps) => {
  const { getContentScoresByWeek } = useContentService();
  const { mainTimePeriod, comparisonTimePeriod } = useAppSelector((state) => state);

  const [contentMetricsData, setContentMetricsData] = useState<{
    primarySeries: SplineChartV2Series;
    comparisonSeries: SplineChartV2Series;
    formattedPrimaryTotal: string;
    formattedPercentChange: string;
    percentChange: number;
    isLoading: boolean;
  }>({
    primarySeries: { id: 'primary', name: 'Primary', data: [] },
    comparisonSeries: { id: 'comparison', name: 'Comparison', data: [] },
    formattedPrimaryTotal: '0%',
    formattedPercentChange: '0%',
    percentChange: 0,
    isLoading: true
  });

  useEffect(() => {
    const fetchData = async () => {
      setContentMetricsData((prev) => ({ ...prev, isLoading: true }));
      
      try {
        const contentScores = await getContentScoresByWeek({
            contentMetricType: ContentMetricType.ContentUtilizationScore,
            mainTimePeriod,
            comparisonTimePeriod
        });
        
        const { primaryMetrics, secondaryMetrics } = contentScores.chartData;
        
        const mostRecentPrimaryData = primaryMetrics.length > 0 ? primaryMetrics[primaryMetrics.length - 1][1] : 0;
        
        const matchingComparisonIndex = primaryMetrics.length - 1;
        const mostRecentSecondaryData =
          secondaryMetrics.length > matchingComparisonIndex && matchingComparisonIndex >= 0
            ? secondaryMetrics[matchingComparisonIndex][1]
            : secondaryMetrics.length > 0
            ? secondaryMetrics[secondaryMetrics.length - 1][1]
            : 0;
        
        const calculatedPercentChange = calculatePercentChange(mostRecentPrimaryData, mostRecentSecondaryData);
        
        setContentMetricsData({
          primarySeries: convertToSplineV2Series(primaryMetrics, 'primary', 'Primary'),
          comparisonSeries: convertToSplineV2Series(secondaryMetrics, 'comparison', 'Comparison'),
          formattedPrimaryTotal: `${(mostRecentPrimaryData * 100).toFixed(2)}%`,
          formattedPercentChange: `${Math.min(Math.abs(Math.round(calculatedPercentChange * 100 * 100) / 100), 1000).toFixed(2)}%`,
          percentChange: calculatedPercentChange,
          isLoading: false
        });
      } catch (error) {
        console.error('Error fetching content metrics data:', error);
        setContentMetricsData((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTimePeriod, comparisonTimePeriod]);

  if (contentMetricsData.isLoading) {
    return <MetricSummaryTrendCardLoading variant={variant} />;
  }

  return (
    <MetricSummaryTrendCardV2
      variant={variant}
      title={title || 'Content'}
      value={contentMetricsData.formattedPrimaryTotal}
      change={contentMetricsData.formattedPercentChange}
      direction={getDirection(contentMetricsData.percentChange)}
      primarySeries={contentMetricsData.primarySeries}
      comparisonSeries={contentMetricsData.comparisonSeries}
      metricType={METRICTYPE.PERCENT}
      selected={false}
      onClick={onClick}
    />
  );
};

export default ContentKeyMetricCard; 